import { Skeleton } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ActionPlanService } from "../../api/ActionPlanService";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import SurveyDispatchManager from "../../models/surveyDispatchManager";
import { ActivityResponse } from "../../types/actionPlan";
import { ActionPlanTimeline } from "../ActionPlanTimeline/ActionPlanTimeline";
import { NoDataState } from "../NoDataState/NoDataState";
import { Snackbar } from "../ui/Snackbar/Snackbar";

interface ActionPlanOverviewProps {
  dispatchManager?: SurveyDispatchManager;
  activities?: Activity[];
  error?: boolean;
  isLoading?: boolean;
  hasSurveyAccess?: boolean;
  now: Date;
  onActivitiesRefetch?: () => void;
}
export const ActionPlanOverview = ({
  dispatchManager,
  activities,
  error,
  isLoading,
  hasSurveyAccess,
  now,
  onActivitiesRefetch,
}: ActionPlanOverviewProps) => {
  const { t } = useTranslation("actionPlanPage");
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const actionPlanService = new ActionPlanService();

  const updateActivityMutation = useMutation({
    mutationFn: (activity: ActivityResponse) =>
      actionPlanService.updateActivity(activity),
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activity: ActivityResponse) =>
      actionPlanService.deleteActivity(activity),
  });

  const createActivitiesMutation = useMutation({
    mutationFn: (activities: ActivityResponse[]) =>
      actionPlanService.createActivities(activities),
  });

  const handleActivityUpdate = async (activity: Activity) => {
    await updateActivityMutation.mutateAsync(activity.toAPI());
    setSnackBarOpen(true);
    onActivitiesRefetch?.();
  };

  const handleActivitiesAdd = async (newActivities: Activity[]) => {
    await createActivitiesMutation.mutateAsync(
      newActivities.map((a) => a.toAPI())
    );
    setSnackBarOpen(true);
    onActivitiesRefetch?.();
  };

  const handleActivityRemove = async (activity: Activity) => {
    await deleteActivityMutation.mutateAsync(activity.toAPI());
    setSnackBarOpen(true);
    onActivitiesRefetch?.();
  };

  if (isLoading)
    return <Skeleton variant="rounded" width="100%" height={200} />;
  if (error) {
    return (
      <NoDataState title={t("fetchError.title")} info={t("fetchError.info")} />
    );
  }
  return (
    <div>
      {activities && (
        <ActionPlanTimeline
          dispatchManager={dispatchManager}
          activities={activities}
          onActivitiesRefetch={onActivitiesRefetch}
          onActivityUpdate={handleActivityUpdate}
          onActivityRemove={handleActivityRemove}
          onActivitiesAdd={handleActivitiesAdd}
          now={now}
          hasSurveyAccess={hasSurveyAccess}
        />
      )}
      <Snackbar
        open={snackBarOpen}
        onClose={() => setSnackBarOpen(false)}
        message={t("feedback.success")}
        autoHideDuration={3000}
      />
    </div>
  );
};
